import { useForm } from "@mantine/form";

import {
  IssueStatus,
  IssueType,
  ProjectIssueFragment,
} from "@/generated/graphql";

export type LocalProjectIssueFormState = Partial<ProjectIssueFragment> & {
  isNew: boolean;
};

export type FormState = {
  issueFormState: LocalProjectIssueFormState;
};

export const getIssueFormState = (
  selectedIssue?: ProjectIssueFragment
): LocalProjectIssueFormState => {
  return {
    title: selectedIssue?.title ?? "",
    description: selectedIssue?.description ?? "",
    dueDate: selectedIssue?.dueDate ?? new Date().toISOString(),
    assignees: selectedIssue?.assignees,
    status: selectedIssue?.status ?? IssueStatus.NotStarted,
    type: selectedIssue?.type ?? IssueType.Task,
    parentId: selectedIssue?.parentId,
    deleted: !!selectedIssue?.deleted,
    isNew: !selectedIssue?.id, // Fixed: should be true when no ID (new issue)
    id: selectedIssue?.id,
    groupId: selectedIssue?.groupId,
  };
};

export const defaultState = {
  title: "",
  description: "",
  dueDate: new Date().toISOString(),
  assignees: [],
  status: IssueStatus.NotStarted,
  type: IssueType.Task,
  parentId: null,
  deleted: false,
  isNew: true,
  id: undefined,
  groupId: undefined,
};

export function useIssueForm(
  selectedIssue?: ProjectIssueFragment,
  milestoneOptionsEnabled: boolean = false
) {
  const form = useForm<FormState>({
    mode: "uncontrolled",
    initialValues: { issueFormState: getIssueFormState(selectedIssue) },
    validate: {
      issueFormState: {
        title: (value) => {
          if (!value || value.trim().length === 0) {
            return "Task name is required";
          }
          if (value.trim().length < 3) {
            return "Task name must be at least 3 characters long";
          }
          if (value.trim().length > 100) {
            return "Task name must be less than 100 characters";
          }
          return null;
        },
        description: (value) => {
          if (value && value.length > 200) {
            return "Description must be less than 200 characters";
          }
          return null;
        },
        dueDate: (value) => {
          if (!value) {
            return "Due date is required";
          }

          return null;
        },
        assignees: (value) => {
          if (!value) {
            return "An assignee is required";
          }

          return null;
        },
        parentId: (value) => {
          // Only validate parentId if milestone options are enabled
          if (
            milestoneOptionsEnabled &&
            (!value || value.trim().length === 0)
          ) {
            return "Milestone selection is required";
          }
          return null;
        },
      },
    },
  });

  const resetForm = () => {
    form.setValues({ issueFormState: defaultState });
    form.clearErrors();
  };

  const updateProjectIssue = (updatedProjectIssue: ProjectIssueFragment) => {
    const curr = form.getValues().issueFormState;
    form.setValues({
      issueFormState: getIssueFormState({ ...curr, ...updatedProjectIssue }),
    });
  };

  const validateForm = () => {
    return form.validate();
  };

  return {
    form,
    resetForm,
    updateProjectIssue,
    validateForm,
  };
}
